import { useTranslation } from 'react-i18next';

import type { AdminInvoice } from './types';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';

interface Props {
  invoice: AdminInvoice;
  outstanding: string;
}

function formatMoney(amount: string, currency: string): string {
  return `${amount} ${currency}`;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

export function InvoiceDetailBody({ invoice, outstanding }: Props): JSX.Element {
  const { t } = useTranslation('adminBilling');
  return (
    <>
      <div className="flex items-center gap-3">
        <InvoiceStatusBadge status={invoice.status} />
        <span className="text-sm text-muted-foreground">
          {t('detail.outstanding', {
            amount: outstanding,
            currency: invoice.currency,
          })}
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <dt className="text-muted-foreground">{t('detail.total')}</dt>
        <dd className="font-medium">
          {formatMoney(invoice.total_amount, invoice.currency)}
        </dd>
        <dt className="text-muted-foreground">{t('detail.subtotal')}</dt>
        <dd>{formatMoney(invoice.subtotal_amount, invoice.currency)}</dd>
        <dt className="text-muted-foreground">{t('detail.tax')}</dt>
        <dd>{formatMoney(invoice.tax_amount, invoice.currency)}</dd>
        <dt className="text-muted-foreground">{t('detail.amount_paid')}</dt>
        <dd>{formatMoney(invoice.amount_paid, invoice.currency)}</dd>
        <dt className="text-muted-foreground">{t('detail.issued_at')}</dt>
        <dd>{formatDate(invoice.issued_at)}</dd>
        <dt className="text-muted-foreground">{t('detail.due_at')}</dt>
        <dd>{formatDate(invoice.due_at)}</dd>
        <dt className="text-muted-foreground">{t('detail.paid_at')}</dt>
        <dd>{formatDate(invoice.paid_at)}</dd>
      </dl>
      {invoice.notes ? (
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
          <p className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
            {t('detail.notes')}
          </p>
          {invoice.notes}
        </div>
      ) : null}
    </>
  );
}
